import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { CButton, CCloseButton, CForm, CModal, CModalBody, CModalHeader } from '@coreui/react-pro';

import { callApi } from '../../../apiCaller';
import { formatEventSingleDateTime, getLocationLinkEvent, getTimeCustomDate, getURLParams, removeURLParameter, toastError } from '../../../utils';
import {
     CALENDAR_OPTIONS,
     DEFAULT_DATE_FORMAT,
     LOCAL_DIRECT_PATH,
     LOCATION_TYPE,
     MY_LEARNING_PATH,
     NOTIFICATION_PAGE_SIZE,
     TIME_ZONE_EST,
} from '../../../constants';

import { SimpleSelectGroup } from '../Forms';
import { Waiting } from '../Loadings';
import AddToCalendarButton from '../../CMS/Subscriber/MyLeaning/Trainings/GetConfigs/AddToCalendarButton';

import { setShow, setShowEditPopup, setShowPopupDetail } from '../../../actions/general';
import { fetchNotificationsRequest } from '../../CMS/Subscriber/MyLeaning/actions';
import { fetchCoursesRequest } from '../../CMS/Subscriber/MyLeaning/Course/actions';
import { fetchSubscriberSellingGroupsRequest } from '../../CMS/Guest/SellingGroups/actions';

const CHOOSE_TRAINING_EVENT_DATE = {
     TRAINING_EVENT_DATE: 'Choose a training event date',
     TRAINING_DATE_SELECTED: 'Training date selected',
};

let availableEventCalled = false;

//#region Fetch API
const API_SUBSCRIBER_AVAILABLE_PRODUCT_EVENT_CREDIT = 'products/:productId/:userId/:courseId/available-events';
const API_SUBSCRIBER_CONFIRM_EVENT_FOR_EVENT_CREDIT = 'products/product-credit/confirm-event';

export const fetchAvailableEvents = async (nextDispatch) => {
     const { productId, userId, courseId, setIsLoading } = nextDispatch;

     if (availableEventCalled) return;

     availableEventCalled = true;
     setIsLoading(true);

     const endpoint = API_SUBSCRIBER_AVAILABLE_PRODUCT_EVENT_CREDIT.replace(':productId', productId)
          .replace(':userId', userId)
          .replace(':courseId', courseId);

     return await callApi(endpoint, 'GET')
          .then((response) => {
               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               return response.data;
          })
          .finally(() => {
               availableEventCalled = false;
               setIsLoading(false);
          });
};

export const confirmEventDate = async (payload) => {
     return await callApi(API_SUBSCRIBER_CONFIRM_EVENT_FOR_EVENT_CREDIT, 'PATCH', payload).then((response) => {
          if (response.status !== 200) {
               toastError(response);
               return;
          }

          return response.data;
     });
};
//#endregion

const validationSchema = (setValidateOnChange) => {
     setValidateOnChange(true);
     return Yup.object().shape({
          eventDate: Yup.string().required('Field is required.'),
     });
};

const PopupChooseTrainingEventDate = () => {
     const dispatch = useDispatch();
     const navigate = useNavigate();

     const productId = useSelector((state) => state.general.showPopupDetail);
     const userProductId = useSelector((state) => state.general.showEditPopup);
     const user = useSelector((state) => state.subscriber.user);
     const products = useSelector((state) => state.subscriber.products.list);
     const courses = useSelector((state) => state.subscriber.courses);

     const [validateOnChange, setValidateOnChange] = useState(false);
     const [isLoading, setIsLoading] = useState(false);
     const [isSubmitting, setIsSubmitting] = useState(false);
     const [currentStep, setCurrentStep] = useState(CHOOSE_TRAINING_EVENT_DATE.TRAINING_EVENT_DATE);
     const [eventAvailableList, setEventAvailableList] = useState([]);
     const [eventSelected, setEventSelected] = useState({});

     const localDirectPath = localStorage.getItem(LOCAL_DIRECT_PATH) || '';
     const searchString = localDirectPath.split(/[?|#]/)[1];
     const queryObject = getURLParams(searchString);

     const organization = courses?.find((course) => course.id === userProductId && course.seat?.organization);
     const organizationId = organization?.seat.organizationId;

     const product = useMemo(() => {
          if (!productId) return {};

          return products.find((product) => product.id === productId) || {};
     }, [products, productId]);

     const initialValues = {
          productName: product.name || '',
          eventDate: '',
     };

     const isChooseTrainingEventDate = currentStep === CHOOSE_TRAINING_EVENT_DATE.TRAINING_EVENT_DATE;

     // #region Handle event calendar props
     const isLocationTypeOnline = eventSelected.locationType === LOCATION_TYPE.ONLINE;
     const eventLocation = eventSelected.location ?? '';

     let eventName = eventSelected.newName ?? '';

     let eventCalendarDescription = isLocationTypeOnline
          ? `Join the meeting using Zoom with the information below:`
          : `Attend the meeting in-person with the information below:`;

     if (Object.keys(eventSelected).length) {
          if (eventSelected.startAt === null) {
               eventName = getTimeCustomDate(eventSelected.name);
          } else {
               const startTime = formatEventSingleDateTime(eventSelected.startAt);
               eventName = `${product.genericPublicName} - ${startTime}`;
          }

          if (isLocationTypeOnline) {
               eventCalendarDescription += ` → [url]${getLocationLinkEvent(eventLocation)}[/url]`;
          } else {
               eventCalendarDescription += ` [Address]: ${eventLocation} [Notes]: ${eventSelected.locationNotes ?? ''}`;
          }
     }

     const eventCalendarProps = {
          label: 'Add To Your Calendar',
          name: eventName,
          description: eventCalendarDescription,
          iCalFileName: 'Reminder-Event',
          calendarOptions: CALENDAR_OPTIONS,
          locationDescription: `${eventSelected.locationType === LOCATION_TYPE.IN_PERSON ? eventLocation : ''}`,
          startDate: eventSelected.startAt ? dayjs(eventSelected.startAt).format(DEFAULT_DATE_FORMAT) : 'today',
          endDate: eventSelected.endAt ? dayjs(eventSelected.endAt).format(DEFAULT_DATE_FORMAT) : 'today',
          startTime: eventSelected.startAt ? dayjs(eventSelected.startAt).format('HH:mm') : '00:00',
          endTime: eventSelected.endAt ? dayjs(eventSelected.endAt).format('HH:mm') : '00:00',
          timeZone: eventSelected.timezone ?? TIME_ZONE_EST,
          trigger: 'click',
     };
     // #endregion Handle event calendar props

     // Fetch available event list
     useEffect(() => {
          if (!product.id || !user?.id) return;

          const nextDispatch = {
               productId: product.id,
               userId: user.id,
               courseId: userProductId,
               setIsLoading,
               setEventAvailableList,
               handleClosePopup,
          };

          (async () => {
               // Mapping available events
               const availableEventsResult = await fetchAvailableEvents(nextDispatch);

               if (!availableEventsResult) return;

               if (availableEventsResult[0] && !Object.keys(availableEventsResult[0]).length) {
                    handleClosePopup();
                    return;
               }

               const formattedEvents = availableEventsResult.map((event) => {
                    return {
                         ...event,
                         newName: formatEventSingleDateTime(event.startAt),
                    };
               });

               setEventAvailableList(formattedEvents);
          })();
     }, [product, user]); // eslint-disable-line react-hooks/exhaustive-deps

     const removeParameterShowPopup = () => {
          if (queryObject.courseId) {
               const directPathFinal = removeURLParameter(localDirectPath, 'courseId', false);
               navigate(directPathFinal, { replace: true });
          }
     };

     const handleClosePopup = () => {
          dispatch(setShowPopupDetail(null));
          dispatch(setShowEditPopup(null));
          dispatch(setShow(null));
          removeParameterShowPopup();
     };

     const handleFormSubmit = async () => {
          setIsSubmitting(true);

          const payload = {
               productId,
               eventId: eventSelected.id,
               userProductId,
               organizationId,
          };

          const confirmEventResult = await confirmEventDate(payload);
          setIsSubmitting(false);

          if (!confirmEventResult) return;

          dispatch(fetchSubscriberSellingGroupsRequest());
          dispatch(fetchNotificationsRequest({ pageSize: NOTIFICATION_PAGE_SIZE, page: 0 }));
          dispatch(fetchCoursesRequest());
          removeParameterShowPopup();
          setCurrentStep(CHOOSE_TRAINING_EVENT_DATE.TRAINING_DATE_SELECTED);
     };

     return (
          <CModal visible={true} alignment="center" className="popup-custom popup-choose-event-date">
               <CModalHeader closeButton={false}>
                    <h3 className="popup-title text-uppercase">
                         {isChooseTrainingEventDate
                              ? CHOOSE_TRAINING_EVENT_DATE.TRAINING_EVENT_DATE
                              : CHOOSE_TRAINING_EVENT_DATE.TRAINING_DATE_SELECTED}
                    </h3>
                    <CCloseButton onClick={handleClosePopup} />
               </CModalHeader>
               <CModalBody>
                    <Formik
                         initialValues={initialValues}
                         validationSchema={() => validationSchema(setValidateOnChange)}
                         validateOnChange={validateOnChange}
                         onSubmit={handleFormSubmit}
                         validateOnBlur={false}
                         enableReinitialize
                    >
                         {({ values, errors, setFieldValue, handleSubmit }) => {
                              const eventDateName = eventSelected.newName || '';

                              const description = isChooseTrainingEventDate
                                   ? `You have one credit for ${values.productName} that can be redeemed to attend a training event. Select from the available dates. Check back in the future for additional dates, which are added regularly.`
                                   : `You've chosen the following training date: ${eventDateName}. We recommend adding this to your calendar now, then visit details page for your training.`;

                              const disabledButton = isSubmitting || !!Object.keys(errors).length;

                              const handleChangeSelect = (e) => {
                                   const value = e.target.value;
                                   const selectedEvent = eventAvailableList.find((event) => event.id === value);

                                   setFieldValue('eventDate', value);

                                   if (selectedEvent) {
                                        setEventSelected(selectedEvent);
                                   }
                              };

                              return (
                                   <>
                                        <p className="popup-description text-center">{description}</p>

                                        {isChooseTrainingEventDate ? (
                                             <CForm onSubmit={handleSubmit} noValidate>
                                                  <SimpleSelectGroup
                                                       id="eventDate"
                                                       classNameInput="input-custom select-search-custom"
                                                       typeName={'newName'}
                                                       isRequired
                                                       disabled={isLoading}
                                                       optionNameDefault="Select a training date"
                                                       data={eventAvailableList}
                                                       value={values.eventDate}
                                                       error={errors.eventDate}
                                                       handleChange={handleChangeSelect}
                                                  />

                                                  <div className="text-center">
                                                       <CButton
                                                            className="btn-blue-highlight btn-round-9 btn-h-50 text-uppercase"
                                                            type="submit"
                                                            disabled={disabledButton}
                                                       >
                                                            <Waiting isLoading={isSubmitting}>Confirm My Date</Waiting>
                                                       </CButton>
                                                  </div>
                                             </CForm>
                                        ) : (
                                             <div className="text-center">
                                                  <AddToCalendarButton
                                                       eventCalendar={eventCalendarProps}
                                                       className={classNames({
                                                            disabled: !eventSelected.startAt || !eventSelected.endAt,
                                                       })}
                                                  />
                                                  <Link
                                                       to={MY_LEARNING_PATH.COURSE_EVENT_DETAILS.replace(':slug', product.slug).replace(
                                                            ':eventId',
                                                            eventSelected.id
                                                       )}
                                                       className="visit-detail-page"
                                                       onClick={handleClosePopup}
                                                  >
                                                       Visit Training Details Page
                                                  </Link>
                                             </div>
                                        )}
                                   </>
                              );
                         }}
                    </Formik>
               </CModalBody>
          </CModal>
     );
};

export default PopupChooseTrainingEventDate;
